import React, { useEffect, useRef, useState } from "react";
import { ReactComponent as BookmarkIcon } from "assets/icons/bookmark.svg";
import { ReactComponent as QuestionIcon } from "assets/icons/question.svg";
import { ReactComponent as RemoveIcon } from "assets/icons/remove.svg";
import { ReactComponent as LocationIcon } from "assets/icons/location.svg";
import { formatCurrency } from "utils/formatNumber";
import TooltipCursor from "components/Tooltip/TooltipCursor";
import RemoveWarningDialog from "components/Dialog/RemoveWarningDialog";
import useBookmark from "hooks/useBookmark";
import { formatPropertyLocation } from "utils/formatText";
import ChooseAndCreateOrganiseList from "components/Dialog/ChooseAndCreateOrganiseList";
import useProperties from "hooks/useProperties";
import PropertyNoteDialog from "components/Dialog/PropertyNoteDialog";
import SearchResultPropertyCardImageSlider from "./SearchResultPropertyCardImageSlide";
import { usePropertyActions } from "hooks/usePropertyActions";

const SearchResultPropertyCard = ({ property, isOrganisePage }) => {
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef(null);
  const { bookmarkProperty, loading } = useBookmark();
  const {
    isBookmarked,
    isDoubtProperty,
    isDiscardedProperty,
    handleAction,
    handleRemoveConfirm,
    removeWarningPopupOpen,
    setRemoveWarningPopupOpen,
    isBookmarkPropertyHasNote,
    chooseOrganiseList,
    setChooseOrganiseList,
    handleUpdateNote,
    handleAddBookmark,
  } = usePropertyActions(property);

  // Show full title if the property title is truncated
  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        const { scrollHeight, clientHeight } = textRef.current;
        setIsTruncated(scrollHeight > clientHeight);
      }
    };

    checkTruncation();
    window.addEventListener("resize", checkTruncation);
    return () => window.removeEventListener("resize", checkTruncation);
  }, [property?.title]);

  return (
    <>
      <div className="w-full property-box overflow-hidden h-[302px] border-[1px] border-[#F0F0F0] rounded-[8px] flex flex-col">
        <div className="grid grid-cols-1">
          <SearchResultPropertyCardImageSlider
            isBookmarked={isBookmarked}
            property={property}
            isOrganisePage={isOrganisePage}
            isBookmarkPropertyHasNote={isBookmarkPropertyHasNote}
          />
        </div>
        <div className="p-3 bg-white relative">
          <div className="absolute right-[15px] -top-[28.8px] h-[64px] w-[156px] z-[1] bg-white rounded-[12px] border-[1px] border-[#F0F0F0] flex items-center p-3">
            <BookmarkIcon
              onClick={(e) => {
                e.stopPropagation();
                handleAction("bookmark");
              }}
              className={`${
                isBookmarked // Differnet bookmarked color for organise page
                  ? isOrganisePage
                    ? "text-[#000000]"
                    : "text-[#7BCD11]"
                  : "hover:text-[#7BCD1133] text-white"
              } cursor-pointer  stroke-black rotate-0 transition-all duration-300 ease-in-out`}
            />
            <hr className="w-[1px] h-full bg-[#F0EFFB] ml-[14px] mr-[9px]" />
            <QuestionIcon
              onClick={(e) => {
                e.stopPropagation();
                handleAction("doubt");
              }}
              className={`${
                isDoubtProperty
                  ? "fill-[#FC9D47]"
                  : "fill-white hover:fill-[#FEEDDA]"
              } cursor-pointer transition-all duration-300 ease-in-out`}
            />
            <hr className="w-[1px] h-full bg-[#F0EFFB] mr-[15.83px] ml-[11px]" />
            <RemoveIcon
              onClick={(e) => {
                e.stopPropagation();
                handleAction("discard");
              }}
              className={`${
                isDiscardedProperty ? "fill-[#FF6D59]" : "fill-white"
              }  cursor-pointer hover:fill-[#FFE2DE] hover: transition-all duration-300 ease-in-out`}
            />
          </div>
          <h2 className="text-black font-bold  text-[20px] tracking-[0%] leading-[24px]">
            {formatCurrency(property.sale_price, property?.sale_currency)}
          </h2>

          <div className="mt-3">
            <div className="flex items-center justify-between xs:justify-normal xs:space-x-[24px] flex-grow">
              <div className="flex items-center space-x-1 leading-[24px] text-[14px] font-normal text-black">
                <span className="font-semibold">{property?.rooms || 0}</span>
                <span>stanze da letto</span>
                <span>|</span>
                <span className="font-semibold">{property?.bedrooms || 0}</span>
                <span>{property?.bedrooms > 1 ? "Bagni" : "Bagno"}</span>
                <span>|</span>
                <span className="font-semibold">
                  {property?.total_area || 0}
                </span>
                <span>mq</span>
              </div>
            </div>
            <div className="relative mt-[3px] flex flex-col group justify-start">
              <TooltipCursor
                content={property?.title}
                delay={200}
                visible={isTruncated}
              >
                <div>
                  <h1
                    ref={textRef}
                    className="text-[14px] font-normal text-black leading-[24px] tracking-[0%] line-clamp-1"
                  >
                    {property?.title}
                  </h1>
                </div>
              </TooltipCursor>
            </div>

            <div className="mt-[3px] flex items-center justify-between">
              <p className="text-[10px] font-normal text-[#596B82] leading-[16px] line-clamp-1">
                {property && formatPropertyLocation(property)}
              </p>
            </div>
          </div>
        </div>
        <RemoveWarningDialog
          warningText={removeWarningPopupOpen}
          isOpen={removeWarningPopupOpen}
          isClose={() => setRemoveWarningPopupOpen(null)}
          onClick={handleRemoveConfirm}
          buttonText2="Annulla"
        />
      </div>
      <PropertyNoteDialog
        id={property?._id}
        onClick={handleUpdateNote}
        loading={loading}
        defaultText={isBookmarkPropertyHasNote || ""}
      />
      <ChooseAndCreateOrganiseList
        isOpen={chooseOrganiseList}
        onClose={() => setChooseOrganiseList(false)}
        onSaveClick={handleAddBookmark}
      />
    </>
  );
};

export default SearchResultPropertyCard;
